import React from 'react';

import {BUTTON_STYLES, buttonDisabled, type ButtonVariant} from '../lib/buttonStyles.js';

// ============================================================================
// Button — the one shared button (Design Constitution §4 Buttons, C04, F09, S01)
// ----------------------------------------------------------------------------
//   variant: primary (one green per page) · secondary · danger (Delete trigger,
//            danger text) · destructive (fill, delete confirm step only)
//   busy:    swaps the label for 'Saving…' (or busyLabel) and blocks clicks;
//            no spinner (F09)
//   fullWidth: the phone-form submit, a full-width 48px pill (S01)
// Disabled keeps the normal style at 45% opacity and never lifts; show a
// one-line gray reason nearby. Hover and keyboard focus use the one Clickable
// pop-out (§2); green buttons cast a green shadow (index.html .wcf-btn rules).
// ============================================================================

type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> & {
  variant?: ButtonVariant;
  busy?: boolean;
  busyLabel?: string;
  fullWidth?: boolean;
  style?: React.CSSProperties;
};

export default function Button({
  variant = 'secondary',
  busy = false,
  busyLabel = 'Saving…',
  fullWidth = false,
  disabled,
  className,
  style,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const off = Boolean(disabled || busy);
  return (
    <button
      {...rest}
      type={type}
      disabled={off}
      aria-busy={busy || undefined}
      data-button={variant}
      className={['wcf-clickable', 'wcf-btn', `wcf-btn--${variant}`, className].filter(Boolean).join(' ')}
      style={{
        ...BUTTON_STYLES[variant],
        ...(fullWidth ? {width: '100%', minHeight: 48} : null),
        ...(off ? buttonDisabled : null),
        ...style,
      }}
    >
      {busy ? busyLabel : children}
    </button>
  );
}
